/**
 * Hero Cockpit Configuration for Poker Reborn
 *
 * All BBS dimensions, offsets, and sizes for the hero cockpit:
 * seat, cards, and hand strength meter.
 *
 * @see src/components/table/HeroView.ts
 * @see docs/screens/room/HERO_VIEW.md
 * @see docs/screens/room/HERO_SEAT.md
 * @see docs/screens/room/HAND_STRENGTH_METER.md
 */

// =============================================================================
// HERO SEAT
// =============================================================================

/**
 * Hero seat dimensions. BBS breakpoints (screen width → pixels).
 * Use discrete: true for sizes, positions.
 *
 * @see src/components/table/HeroView.ts
 * @see docs/screens/room/HERO_SEAT.md
 */
export const HERO_SEAT_SIZE = {
    avatarSize:     { 375: 80, 720: 90, 900: 106, 1440: 125, 1920: 138 },
    badgeFont:      { 375: 10, 720: 9,  1440: 10, 1920: 10 },
    playerInfoBarY: { 375: 46, 720: 58, 900: 65,  1440: 75,  1920: 85 },
    badgeY:         { 375: -32, 720: -40, 1440: -45, 1920: -45 },
    lastActY:       { 375: 39, 720: 49, 900: 56,  1440: 65,  1920: 74 },
};

/**
 * Hero cockpit: seat offset from seat 0 grid position (pixels).
 * BBS breakpoints (screen width → pixel offset). Use discrete: false.
 *
 * Allows hero seat to sit at a different position than opponents at seat 0.
 *
 * @see src/components/table/HeroView.ts
 * @see docs/screens/room/HERO_VIEW.md
 */
export const HERO_SEAT_OFFSET = {
    x: { 1920: -150, 1440: -100, 720: -70, 375: -60 },
    y: { 1920: -65, 1440: -49, 720: -24, 375: -13 },
};

// =============================================================================
// HERO CARDS
// =============================================================================

/**
 * Hero cockpit: cards offset from hero seat position (pixels).
 * BBS breakpoints (screen width → pixel offset). Use discrete: false.
 *
 * @see src/components/table/HeroView.ts
 * @see docs/screens/room/HERO_VIEW.md
 */
export const HERO_CARDS_OFFSET = {
    x: { 1920: 140, 1440: 160, 720: 110, 375: 80 },
    y: { 1920: 0, 1440: 0, 720: 0, 375: 0 },
};

/**
 * Hero card width (pixels) at each screen-width breakpoint.
 * BBS smooth interpolation (discrete: false).
 *
 * @see src/components/cards/HeroCardsLayout.ts
 * @see docs/screens/room/CARDS_HERO.md
 */
export const HERO_CARD_WIDTH: Record<number, number> = {
    1920: 99,
    1440: 89,
    720: 64,
    375: 57,
};

// =============================================================================
// HAND STRENGTH METER
// =============================================================================

/**
 * Hand strength meter offset relative to hero seat position.
 * BBS breakpoints (screen width → pixel offset). Use discrete: false.
 *
 * @see src/components/table/HandStrengthMeter.ts
 * @see docs/screens/room/HAND_STRENGTH_METER.md
 */
export const HAND_STRENGTH_METER_OFFSET = {
    x: { 1920: 250, 1440: 80, 720: 58, 375: 50 },
    y: { 1920: 0, 1440: 0, 720: 0, 375: 0 },
};

/**
 * Hand strength meter dimensions.
 * Sized to match hero card height (≈ avatar height).
 * BBS breakpoints (screen width → pixels). Use discrete: false.
 *
 * @see src/components/table/HandStrengthMeter.ts
 * @see docs/screens/room/HAND_STRENGTH_METER.md
 */
export const HAND_STRENGTH_METER_SIZE = {
    barWidth:      { 1920: 18, 1440: 16, 720: 12, 375: 10 },
    barHeight:     { 1920: 138, 1440: 125, 720: 90, 375: 80 },
    cornerRadius:  { 1920: 6,  1440: 6,  720: 5,  375: 3 },
    labelFontMin:  { 1920: 12, 1440: 11, 720: 10, 375: 9 },
    labelFontMax:  { 1920: 16, 1440: 15, 720: 13, 375: 11 },
    labelGapX:     { 1920: 6,  1440: 5,  720: 4,  375: 3 },
    labelMaxWidth: { 1920: 70, 1440: 60, 720: 50, 375: 42 },
};
